/**
 * Job Resumer Utility
 * 
 * Handles resuming upload jobs by re-reading files from directory handles
 */

import { type UploadJob, type UploadJobFile } from './persistentUploadQueue';
import { scanDirectoryHandle } from './folderScanner';

/**
 * Re-read files from a directory handle for resuming
 * Matches files by relative path
 */
export async function reReadJobFiles(
  job: UploadJob,
  directoryHandle: any // FileSystemDirectoryHandle
): Promise<Map<string, File>> {
  const fileMap = new Map<string, File>();
  
  if (!job.useFileSystemAPI || !directoryHandle) {
    throw new Error('Cannot re-read files: directory handle not available');
  }
  
  // Re-scan directory, including directory name so relative paths match the original job
  const scannedFiles = await scanDirectoryHandle(directoryHandle, [], directoryHandle.name);
  
  // Match files by relative path
  for (const scanned of scannedFiles) {
    const jobFile = job.files.find(f => f.relativePath === scanned.relativePath);
    if (jobFile) {
      fileMap.set(scanned.relativePath, scanned.file);
    }
  }
  
  return fileMap;
}

/**
 * Get pending files from a job
 */
export function getPendingJobFiles(job: UploadJob): UploadJobFile[] {
  return job.files.filter(f => 
    f.status === 'pending' || f.status === 'failed'
  );
}

/**
 * Get completed files from a job
 */
export function getCompletedJobFiles(job: UploadJob): UploadJobFile[] {
  return job.files.filter(f => f.status === 'completed');
}

/**
 * Check if a job can be resumed
 */
export function canResumeJob(job: UploadJob): boolean {
  return (
    (job.status === 'active' || job.status === 'paused') &&
    job.completedFiles < job.totalFiles
  );
}

/**
 * Prepare job for resumption
 * Returns files that need to be uploaded
 */
export function prepareJobForResume(job: UploadJob): {
  pendingFiles: UploadJobFile[];
  completedCount: number;
  failedCount: number;
} {
  const pendingFiles = getPendingJobFiles(job);
  
  return {
    pendingFiles,
    completedCount: job.completedFiles,
    failedCount: job.failedFiles,
  };
}

